import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { SizeMe } from 'react-sizeme';

import Header from './Header';
import Player from './Player';
import Stopwatch from './Stopwatch';
import Overlay from './Overlay';
import Close from './Close';

import * as crossService from '../services/cross';

type VisibleType = {
  visible: boolean;
};

type Countdown = {
  current: number;
  previous: number;
}

const motionIn = {
  duration: 0.7,
  ease: [0.6, -0.05, 0.01, 0.99]
};
const motionOut = {
  duration: 0.2
};
const motionBack = {
  initial: {
    x: 40,
    opacity: 0 
  },
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      delay: 0.3,
      ...motionIn
    }
  },
  exit: {
    x: 40,
    opacity: 0,
    transition: {
      ...motionOut
    }
  }
};
const motionProgram = {
  initial: {
    y: -20,
    opacity: 0
  },
  animate: {
    y: 0,
    opacity: 1,
    delay: 0.5,
    transition: {
      ...motionIn
    }
  },
  exit: {
    y: -20,
    opacity: 0,
    transition: {
      delay: 0.3,
      ...motionOut
    }
  }
};
const motionListBlock = {
  animate: {
    transition: {
      delayChildren: 0.2,
      staggerChildren: 0.15
    }
  },
  exit: {
    transition: {
      staggerChildren: 0.05
    },
  }
};
const motionList = {
  initial: {
    x: 40,
    opacity: 0 
  },
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      ...motionIn
    }
  },
  exit: {
    x: 40,
    opacity: 0,
    transition: {
      ...motionOut
    }
  }
};
const countdownGoingIn = {
  initial: {
    y: '-200%',
    opacity: 0
  },
  animate: {
    y: 0,
    opacity: 1
  }
};
const countdownGoingOut = {
  initial: {
    y: '0',
    opacity: 1
  },
  animate: {
    y: '80%',
    opacity: 0,
  }
};
const pulse = keyframes`
  0%, 100% {
    margin: 0 45px;
  }
  50% {
    margin: -5px 30px;
  }
`;
const LinkBackContainer = styled(motion.span)`
  display: inline-block;
`;
const LinkBack = styled(Link)`
  color: #FFFFFF;
  text-decoration: none;
  display: inline-block;
  padding-bottom: 15px;
`;
const LinkBackIcon = styled.svg`
  width: 30px;
  height: 30px;
`;
const Program = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 20px;
`;
const Title = styled.h2`
  font-weight: 700;
  font-size: 18px;
  line-height: 18px;
  margin: 0;  
`;
const ListCointainer = styled(motion.div)`
  &:not(:first-child) ${Title} {
    margin-top: 20px;
  }
`;
const List = styled.ol`
  padding: 0;
  margin: 0;
  list-style-type: circle;
`;
const ListItem = styled.li`
  margin-left: 1rem;
  line-height: 22px;
`;
const ListItemBold = styled(ListItem)`
  font-weight: 600;
`;
const PlayerLink = styled.a`
  color: #FFFFFF;
`;
const PlayerContainer = styled.div<VisibleType>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(100% - 40px);
  transform: translateY(calc(-50% - 20px)) translateX(-50%) scale(${props => props.visible ? 1: 0});
  display: flex;
  flex-direction: column;
  max-width: calc(178vh - 169px);
  margin: 20px 0;
  transition: all 0.3s ease;
`;
const PlayerClose = styled.a`
  display: inline-block;
  color: #FFFFFF;
  margin: 0 0 20px auto;
  height: 30px;
`;
const PlayerWrapper = styled(motion.div)`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  background-color: #000000;

  & iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;
const StopwatchLink = styled.a`
  color: #FFFFFF;
`;
const StopwatchContainer = styled.div<VisibleType>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(calc(-50%)) translateX(-50%) scale(${props => props.visible ? 1: 0});
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
`;
const StopwatchText = styled.svg`
  // color: #FFFFFF;
  margin: 0 45px;
  overflow: hidden;
`;
const StopwatchTextStart = styled(StopwatchText)`
  animation: ${pulse} 1.5s linear infinite;
  cursor: pointer;
`;
const StopwatchTextCountdown = styled.svg`
  // color: #FFFFFF;
  padding: 25px;
  overflow: hidden;
  position: absolute;
`;
const StopwatchCountdownTime = styled(motion.text)`
  transition: all 0.2s;
`;

interface StopWatchDuration {
  sets: number;
  work: number;
};

interface IOwnProps {
  programmes: crossService.Programme[];
};

interface IRouteParams {
  id: string;
};

type Props = IOwnProps & RouteComponentProps<IRouteParams>;

function WorkoutItem(props: Props) {
  const countdownTime = 5;
  const [videoUrl, setVideoUrl] = React.useState<string>('');
  const [showVideoOverlay, setShowVideoOverlay] = React.useState<boolean>(false);
  const [showVideoContainer, setShowVideoContainer] = React.useState<boolean>(false);
  const [showVideoClose, setShowVideoClose] = React.useState<boolean>(false);
  const [initialStopwatchDuration, setInitialStopwatchDuration] = React.useState<StopWatchDuration | undefined>(undefined);
  const [showStopwatchOverlay, setShowStopwatchOverlay] = React.useState<boolean>(false);
  const [showStopwatchContainer, setShowStopwatchContainer] = React.useState<boolean>(false);
  const [showStopwatchClose, setShowStopwatchClose] = React.useState<boolean>(false);
  const [stopwatchCountdown, setStopwatchCountdown] = React.useState<Countdown>({current: -1, previous: countdownTime});
  const [stopwatchDuration, setStopwatchDuration] = React.useState<StopWatchDuration | undefined>(undefined);
  const programme = props.programmes.find(p => p.id.toString() === props.match.params.id)!;
  //const audio5 = new Audio(`/audio/5.mp3`);

  // const renderTime = ({ remainingTime }) => {
  //   if (remainingTime === 0) {
  //     return <div className="timer">Too lale...</div>;
  //   }
  
  //   return (
  //     <div className="timer">
  //       <div className="text">Remaining</div>
  //       <div className="value">{remainingTime}</div>
  //       <div className="text">seconds</div>
  //     </div>
  //   );
  // };

  React.useEffect(() => {
    if (videoUrl === '') {
      setShowVideoClose(false);
      setShowVideoContainer(false);

      setTimeout(() => {
        setShowVideoOverlay(false);
      }, 300);
    } else {
      setShowVideoOverlay(true);

      setTimeout(() => {
        setShowVideoContainer(true);
      }, 10);

      setTimeout(() => {
        setShowVideoClose(true);
      }, 310);
    }
  }, [videoUrl]);

  React.useEffect(() => {
    //console.log(1);
    //console.log(initialStopwatchDuration);
    if (!initialStopwatchDuration) {
      //console.log(2);
      setShowStopwatchClose(false);
      setShowStopwatchContainer(false);

      setTimeout(() => {
        setShowStopwatchOverlay(false);
      }, 300);
    } else {
      //console.log(3);
      setShowStopwatchOverlay(true);

      setTimeout(() => {
        setShowStopwatchContainer(true);
      }, 10);

      setTimeout(() => {
        setShowStopwatchClose(true);
      }, 310);
    }
  }, [initialStopwatchDuration]);

  React.useEffect(() => {
    // https://www.zapsplat.com/
    if (stopwatchCountdown.current > 0) {
      new Audio(`/audio/${stopwatchCountdown.current}.mp3`).play();

      setTimeout(() => {
        setStopwatchCountdown({current: stopwatchCountdown.current - 1, previous: stopwatchCountdown.current});
        
      }, 1000);
    }

    if (stopwatchCountdown.current === 0) {
      new Audio(`/audio/start3.mp3`).play();

      setStopwatchCountdown({current: -1, previous: countdownTime});

      setStopwatchDuration({ sets: 5, work: 10 })
    }
  }, [stopwatchCountdown]);

  return (
    <motion.div initial="initial" animate="animate" exit="exit">
      <LinkBackContainer variants={motionBack}>
        <LinkBack to={`/`}>
          <LinkBackIcon version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 256 256" xmlSpace="preserve">
            <path d="M149.974,191.146c-1.638,0-3.276-0.625-4.524-1.875l-56.748-56.746c-2.5-2.499-2.5-6.552,0-9.05l56.748-56.747  c2.496-2.5,6.553-2.5,9.049,0c2.5,2.499,2.5,6.552,0,9.05L102.278,128l52.22,52.222c2.5,2.499,2.5,6.552,0,9.05  C153.25,190.521,151.611,191.146,149.974,191.146z M256,128C256,57.42,198.58,0,128,0C57.42,0,0,57.42,0,128  c0,70.58,57.42,128,128,128C198.58,256,256,198.58,256,128z M243.2,128c0,63.521-51.679,115.2-115.2,115.2  c-63.522,0-115.2-51.679-115.2-115.2C12.8,64.478,64.478,12.8,128,12.8C191.521,12.8,243.2,64.478,243.2,128z" fill="currentColor" />
          </LinkBackIcon>
        </LinkBack>
      </LinkBackContainer>
      <Header title={programme.name} />
      <Program variants={motionProgram}>
        <motion.div variants={motionListBlock}>
          {programme.sets.map((set) => {
            return <ListCointainer key={`set-${set.id}`} variants={motionList}>
              {(set.setName !== '' || set.comment !== '' || (set.timerSets && set.timerWork)) && 
                <Title>
                  {set.setName}
                  {set.comment && <React.Fragment> ({set.comment})</React.Fragment>}
                  {/*
                  Temp out to release
                  {(set.timerSets && set.timerWork) &&
                    <React.Fragment> <StopwatchLink href="#" onClick={(e: React.MouseEvent) => { e.preventDefault(); setInitialStopwatchDuration({ sets: set.timerSets!, work: set.timerWork!}); } }><Stopwatch /></StopwatchLink></React.Fragment>
                  } */}
                </Title>
              }
              <List key={`listset-${set.id}`}>
              {set.repetitions.map((repetition, repetitionIndex) => {
                return <ListItem key={`repetition-${repetitionIndex}`}>
                  <React.Fragment>{repetition.repetitions} </React.Fragment>
                  {repetition.exercises.map((exercise, exerciseIndex) => {
                    return <React.Fragment key={`exercise-${exercise.id}`}>
                      {exerciseIndex > 0 && <React.Fragment>/</React.Fragment>}
                      {exercise.name}
                      {repetition.comment && <React.Fragment> ({repetition.comment})</React.Fragment>}
                      {exercise.url && <React.Fragment> [ <PlayerLink href="#" onClick={(e: React.MouseEvent) => { e.preventDefault(); setVideoUrl(exercise.url); } }><Player key={`player-${set.id}-${repetition.id}`} /></PlayerLink> ]</React.Fragment>}
                    </React.Fragment>
                  })}
                </ListItem>
              })}
              {set.rounds &&
                <ListItemBold key="rounds">
                  {set.rounds} runder
                </ListItemBold>
              }
              {set.setNote &&
                <ListItemBold key="setnote">
                  {set.setNote}
                </ListItemBold>
              }
              </List>
            </ListCointainer>
          })}
        </motion.div>
      </Program>
      
      <Overlay visible={showVideoOverlay}>
        <PlayerContainer visible={showVideoContainer}>
          <PlayerClose href="#" onClick={(e: React.MouseEvent) => { e.preventDefault(); setVideoUrl(''); } }>
            <Close visible={showVideoClose} />
          </PlayerClose>
          <PlayerWrapper>
            <iframe src={videoUrl} frameBorder="0" title="video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
          </PlayerWrapper>
        </PlayerContainer>
      </Overlay>

      <Overlay visible={showStopwatchOverlay}>
        <SizeMe monitorHeight={true} monitorWidth={true} noPlaceholder={true}>
          {({ size }) => {
            const circleSize = Math.min((size.height! - 90), size.width! - 40);

            return (
              <StopwatchContainer visible={showStopwatchContainer}>
                <PlayerClose href="#" onClick={(e: React.MouseEvent) => { e.preventDefault(); setInitialStopwatchDuration(undefined); setStopwatchCountdown({current: -1, previous: countdownTime}); } }>
                  <Close visible={showStopwatchClose} />
                </PlayerClose>
{/* https://www.schemecolor.com/red-green-amber-colors.php */}
                <CountdownCircleTimer
                  key={Math.random().toString()}
                  // isPlaying={showStopwatchOverlay}
                  isPlaying={stopwatchDuration !== undefined}
                  duration={initialStopwatchDuration?.sets! * initialStopwatchDuration?.work! || 1}
                  colors={[
                    ['#FFFFFF', 1],
                    ['#D2222D', 0.25],
                    ['#FFBF00', 0.25],
                    ['#238823', 0.25],
                    ['#007000', 0.25],
                  ]}
                  size={circleSize}
                  trailColor="#FFFFFF"
                  strokeWidth={25}
                >
                  {({ remainingTime }) => {
                    if (!stopwatchDuration && stopwatchCountdown.current === -1) {
                      return (
                        <StopwatchTextStart viewBox="0 0 59 18" onClick={(e: React.MouseEvent) => { e.preventDefault(); setStopwatchCountdown({current: countdownTime, previous: countdownTime}); } }>
                          <text x="50%" y="14" fill="currentColor" textAnchor="middle">
                            Start
                          </text>
                        </StopwatchTextStart>
                      );
                    }

                    if (stopwatchCountdown.current > -1) {
                      // https://www.florin-pop.com/blog/2019/03/css-pulse-effect/
                      // https://codesandbox.io/s/d3s70

                      if (stopwatchCountdown.current === countdownTime) {
                        return (
                          <StopwatchTextCountdown viewBox="0 0 18 18">
                            <StopwatchCountdownTime x="50%" y="14" fill="currentColor" textAnchor="middle">
                              {stopwatchCountdown.current}
                            </StopwatchCountdownTime>  
                          </StopwatchTextCountdown>
                        );
                      } else {
                          return (
                            <React.Fragment>
                              <StopwatchTextCountdown viewBox="0 0 18 18">
                                <StopwatchCountdownTime x="50%" y="14" fill="currentColor" textAnchor="middle" variants={countdownGoingOut}>
                                  {stopwatchCountdown.previous}
                                </StopwatchCountdownTime>  
                              </StopwatchTextCountdown>
                              <StopwatchTextCountdown viewBox="0 0 18 18">
                                <StopwatchCountdownTime x="50%" y="14" fill="currentColor" textAnchor="middle" variants={countdownGoingIn}>
                                  {stopwatchCountdown.current}
                                </StopwatchCountdownTime>  
                              </StopwatchTextCountdown>
                            </React.Fragment>
                          );
                      }
                    }

                    if (remainingTime && initialStopwatchDuration) {
                      const roundsCompleted = Math.floor(((initialStopwatchDuration.sets * initialStopwatchDuration.work) - remainingTime) / initialStopwatchDuration.work);
                      const rounds = initialStopwatchDuration.sets - roundsCompleted;
                      const work = remainingTime - ((rounds - 1) * initialStopwatchDuration.work);
                      const minutes = Math.floor((work % 3600) / 60)
                      const seconds = work % 60

                      return (
                        <div>
                          {stopwatchCountdown.current}
                          <div>{rounds}</div>
                          <div>{`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`}</div>
                        </div>
                      );
                    }

                    return (
                      <StopwatchText viewBox="0 0 59 18">
                        <text x="0" y="14" fill="currentColor">
                          Finished
                        </text>
                      </StopwatchText>
                    );
                  }}
                </CountdownCircleTimer>
              </StopwatchContainer>
            )
          }
            
        }
        </SizeMe>
      </Overlay>
    </motion.div>
  );
}

export default withRouter(WorkoutItem);